import Box from '@mui/material/Box'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { validateEmail, validateEmailText } from '../../helpers/validateEmail'
import useEmailMessage from '../../hooks/useEmailMessage'
import { InvitationSource } from '../../swagger'
import ActionButtons from '../Buttons/ActionButtons'
import { ContainedButtonVariant } from '../Buttons/ContainedButton'
import { TextButtonVariant } from '../Buttons/TextButton'
import BasicModal from './BasicModal'
import { EmailFieldsValidationMessageTypes } from './ChangeEmailModal'
import { maskEmail } from '@utils/maskEmail'

export const USE_DIFFERENT_EMAIL = 'USE_DIFFERENT_EMAIL'

export interface SendInviteModalProps {
  isOpen: boolean
  familyName: string
  source?: InvitationSource
  contactEmail?: string
  crmEmails?: string[]
  onClose: () => void
  onSend?: (selectedEmail: string) => void
}

export const SendInviteModal: React.FC<SendInviteModalProps> = ({
  isOpen,
  familyName,
  source,
  contactEmail,
  crmEmails,
  onClose,
  onSend,
}) => {
  const { t } = useTranslation()
  const { validationMessages } = useEmailMessage()
  const [selectedEmailOption, setSelectedEmailOption] = useState<string>('')
  const [customEmail, setCustomEmail] = useState<string>('')
  const [customEmailError, setCustomEmailError] = useState<string>('')

  const availableEmails = useMemo(() => {
    if (source === InvitationSource.ReturningFamily) {
      return contactEmail ? [contactEmail] : []
    }

    if (crmEmails && crmEmails.length > 0) {
      return Array.from(new Set(crmEmails.filter(Boolean)))
    }

    if (contactEmail) {
      return [contactEmail]
    }

    return []
  }, [source, crmEmails, contactEmail])

  useEffect(() => {
    if (isOpen) {
      const defaultSelectedEmail =
        availableEmails.length > 0 ? availableEmails[0] : USE_DIFFERENT_EMAIL

      setSelectedEmailOption(defaultSelectedEmail)
      setCustomEmail('')
      setCustomEmailError('')
    }
  }, [isOpen, availableEmails])

  const isCustomEmail = selectedEmailOption === USE_DIFFERENT_EMAIL

  const handleEmailOptionChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value
    setSelectedEmailOption(value)
    if (value !== USE_DIFFERENT_EMAIL) {
      setCustomEmailError('')
    }
  }

  const validateCustomEmailValue = (value: string): string => {
    const trimmed = value.trim()
    if (!trimmed) {
      return validationMessages(EmailFieldsValidationMessageTypes.NoEmail)
    }
    const errorCode = validateEmailText(trimmed)
    if (errorCode && errorCode !== '') {
      return validationMessages(errorCode)
    }
    return ''
  }

  const handleCustomEmailChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value
    setCustomEmail(value)

    if (customEmailError) {
      const error = validateCustomEmailValue(value)
      setCustomEmailError(error)
    }
  }

  const handleCustomEmailBlur = () => {
    const error = validateCustomEmailValue(customEmail)
    setCustomEmailError(error)
  }

  const isSendDisabled = useMemo(() => {
    if (!isOpen) return true

    if (isCustomEmail) {
      const trimmed = customEmail.trim()
      return trimmed.length === 0 || !validateEmail(trimmed)
    }

    return !selectedEmailOption
  }, [isOpen, isCustomEmail, customEmail, selectedEmailOption])

  const handleSend = () => {
    if (isCustomEmail) {
      const trimmed = customEmail.trim()
      const error = validateCustomEmailValue(trimmed)
      if (error) {
        setCustomEmailError(error)
        return
      }
      onSend?.(trimmed)
    } else if (selectedEmailOption) {
      onSend?.(selectedEmailOption)
    }
  }

  const handleFormSubmit = (event: React.FormEvent<HTMLDivElement>) => {
    event.preventDefault()
    if (!isSendDisabled) {
      handleSend()
    }
  }

  const dialogContent = (
    <Box display="flex" flexDirection="column" sx={{ mt: 1 }}>
      <Typography
        variant="body1"
        align="center"
        sx={{ mb: 3, px: 1, color: 'text.primary' }}
      >
        {t(
          'SendInviteModal.Subtitle',
          'Choose the email address to send the {{familyName}} invite to.',
          { familyName }
        )}
      </Typography>

      <TextField
        select
        fullWidth
        id="sendInviteEmailSelect"
        label={t('SendInviteModal.Email', 'Email')}
        variant="filled"
        value={selectedEmailOption}
        onChange={handleEmailOptionChange}
      >
        {availableEmails.map((email) => (
          <MenuItem key={email} value={email}>
            {source === InvitationSource.ReturningFamily
              ? maskEmail(email)
              : email}
          </MenuItem>
        ))}
        <MenuItem key={USE_DIFFERENT_EMAIL} value={USE_DIFFERENT_EMAIL}>
          {t('SendInviteModal.UseDifferentEmail', 'Use a different email')}
        </MenuItem>
      </TextField>

      {isCustomEmail && (
        <TextField
          fullWidth
          required
          id="sendInviteCustomEmailInput"
          label={t('SendInviteModal.CustomEmailLabel', 'Email address')}
          variant="filled"
          type="email"
          value={customEmail}
          onChange={handleCustomEmailChange}
          onBlur={handleCustomEmailBlur}
          error={Boolean(customEmailError)}
          helperText={customEmailError}
          sx={{ mt: 2 }}
        />
      )}
      {isCustomEmail && source === InvitationSource.ReturningFamily && (
        <Typography variant="body2" color="error" mt={2}>
          {t(
            'SendInviteModal.UseDifferentEmailWarning',
            'Using a different email address will create a new family account. The invitation will not be connected to the returning family account.'
          )}
        </Typography>
      )}
    </Box>
  )

  const dialogActions = (
    <ActionButtons
      primaryButtonLabel={ContainedButtonVariant.SendInvitation}
      disablePrimaryButton={isSendDisabled}
      secondaryButtonLabel={TextButtonVariant.Cancel}
      secondaryClick={onClose}
    />
  )

  return (
    <BasicModal
      isOpen={isOpen}
      dialogTitle={t('SendInviteModal.Title', 'Send Invite')}
      dialogContent={dialogContent}
      dialogActions={dialogActions}
      handleFormSubmit={handleFormSubmit}
      ariaLabel="Send Invite Modal"
      maxWidth="xs"
    />
  )
}

export default SendInviteModal
